/**
 * KEEPING THE TOKEN'S ROLES IN STEP WITH THE MEMBER RECORD.
 *
 * Firestore rules read roles from the signed-in person's ID TOKEN, which is a
 * snapshot minted at sign-in and refreshed about hourly. Callables read the
 * membership record instead, live. So the two can disagree for as long as a
 * token lives, in both directions:
 *
 *   ROLE INCREASED   the callable allows it, the rules still refuse the
 *                    document read behind it, and the screen looks broken
 *   ROLE REDUCED     the callable refuses, but a direct Firestore read the
 *                    rules govern still succeeds until the token turns over
 *
 * The backend already does what it can about the second: `setUserRoles` and
 * `removeUser` revoke refresh tokens, so the session ends at the next refresh.
 * This closes the gap from the other side. The member record is readable by
 * the member themself, so the app watches its own and forces a token refresh
 * the moment the roles on it stop matching the roles in the token — seconds,
 * rather than up to an hour.
 *
 * WHAT THIS DOES NOT DO, and cannot without redesigning authentication: it
 * does not make the change atomic. Between the administrator's write and this
 * refresh landing there is a window, bounded by how quickly the listener fires
 * and the token round-trips, in which a reduced role still passes a rules
 * check. Firebase custom claims propagate on token refresh and that is
 * inherent. What is not left to chance is anything a CALLABLE decides, which
 * is every write and every file URL: those read the membership record and were
 * never stale.
 */

import { useEffect, useRef } from 'react';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { app } from '../lib/firebase';
import { useAuth } from './useAuth';

const db = getFirestore(app);

const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

export function useRoleSync(): void {
  const { session, refresh } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const tokenRoles = session?.roles ?? [];
  /** One refresh per change, not one per snapshot. */
  const refreshing = useRef(false);

  useEffect(() => {
    if (!orgId || !uid) return;
    return onSnapshot(
      doc(db, 'orgs', orgId, 'members', uid),
      (snap) => {
        if (!snap.exists()) return;
        const live = snap.data().roles;
        if (!Array.isArray(live)) return;
        const roles = live.filter((r): r is string => typeof r === 'string');
        if (same(roles, tokenRoles) || refreshing.current) return;
        refreshing.current = true;
        void refresh().finally(() => { refreshing.current = false; });
      },
      () => {
        // A refused or dropped listener is not worth interrupting anybody for.
        // The token still turns over on its own within the hour.
      },
    );
  }, [orgId, uid, tokenRoles.join('|'), refresh]);
}
